import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { AccessModes, Dimensions, Metrics } from 'components/insights/constants';
import { formatPercentage, formatWholeNumber } from 'components/insights/formatting';
import { useNetworkPerformanceStore } from 'stores/networkPerformanceStore';
import { colors } from 'styles/theme/colors';

import MetricDetailLayout from '../../../shared/slideout/MetricDetailLayout';
import { readGrandTotalOrZero, sumByDimensionValue } from '../../../shared/slideout/rollupHelpers';
import { StatTile } from '../../../shared/slideout/statTiles';
import useNetworkPerformanceAnalytics from '../../query/useNetworkPerformanceAnalytics';
import { METRIC_CONFIGS, NetworkPerformanceMetrics } from '../constants';
import { type MetricHeaderProps } from '../NetworkPerformanceSlideout';

import { buildColumnChartOptions } from './chartHelpers';

const config = METRIC_CONFIGS[NetworkPerformanceMetrics.SERVICE_REFUSALS];

const REFUSED_BY_TYPE_PARAMS = {
  source: config.source,
  accessModes: [AccessModes.MANAGER],
  dimensions: [Dimensions.SERVICE_REFUSAL_REFUSED_BY_TYPE],
  metrics: [config.primaryMetric],
  reviewType: config.reviewType,
};

export default function ServiceRefusalsHeader({ loading, rollups, entityRows, entityLabel }: MetricHeaderProps) {
  const selectedGroupType = useNetworkPerformanceStore((s) => s.selectedGroupType);
  const totalRefusals = readGrandTotalOrZero(rollups, Metrics.SERVICE_REFUSALS_COUNT);

  // Side query for refused-by-type breakdown
  const { data: refusedByData, loading: refusedByLoading } = useNetworkPerformanceAnalytics(REFUSED_BY_TYPE_PARAMS, {
    queryKey: 'networkPerformanceSlideout:serviceRefusals:refusedByType',
  });

  const refusedByBreakdown = useMemo(() => {
    const types = refusedByData.dimensions.getCurrentDimensionValuesByIndex(0);
    const counts = refusedByData.metrics.getCurrentSeries(Metrics.SERVICE_REFUSALS_COUNT);
    const byType = sumByDimensionValue(types, counts);
    return {
      patientCount: byType.get('patient') ?? 0,
      facilityCount: byType.get('rehab_facility') ?? 0,
    };
  }, [refusedByData]);

  const patientPct = totalRefusals > 0 ? (refusedByBreakdown.patientCount / totalRefusals) * 100 : 0;
  const facilityPct = totalRefusals > 0 ? (refusedByBreakdown.facilityCount / totalRefusals) * 100 : 0;

  const { options: chartOptions, scrollConfig } = useMemo(
    () =>
      buildColumnChartOptions({
        entityRows,
        seriesName: config.primaryMetricLabel,
        color: colors.accentRed,
        yAxisAllowDecimals: false,
        tooltipFormatter: function (this: Highcharts.TooltipFormatterContextObject) {
          return `${this.key}: ${this.y} ${config.primaryMetricLabel}`;
        },
      }),
    [entityRows]
  );

  const tilesLoading = loading || refusedByLoading;

  return (
    <MetricDetailLayout
      tiles={
        <>
          <StatTile
            label={config.primaryMetricLabel}
            loading={tilesLoading}
            primary={formatWholeNumber(totalRefusals)}
          />
          <StatTile
            label='Refusals by Patient'
            loading={tilesLoading}
            primary={formatWholeNumber(refusedByBreakdown.patientCount)}
            addendum={`${formatPercentage(patientPct)} of total refusals`}
          />
          <StatTile
            label={`Refusals by ${selectedGroupType!.displayName}`}
            loading={tilesLoading}
            primary={formatWholeNumber(refusedByBreakdown.facilityCount)}
            addendum={`${formatPercentage(facilityPct)} of total refusals`}
          />
        </>
      }
      chartTitle={`${config.primaryMetricLabel} by ${entityLabel}`}
      chartLoading={loading}
      isEmpty={entityRows.length === 0}
      scrollConfig={scrollConfig}
      emptyMessage='No service refusals to show for this time period.'>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </MetricDetailLayout>
  );
}
